import { STAGE_SCHEDULE, type StageDefinition } from '@zongheng/core-schema';

/**
 * 光学轨道（Optical Track）—— 参考 RareUI StepPlayer / React Bits Stepper 的思路：
 * 把「顶栏导航 + 16 环节进度」整合成一条轨道；当前环节从圆点拉伸成一条正在充能的胶囊。
 *
 * - 已完成：微光圆点；
 * - 当前：发光菱形 + 环节短名 + 进度充能条；
 * - 未来：冷灰暗点；对辩/结辩节点保留不同刻度节奏。
 */
export function OpticalTrack({
  currentIndex,
  completedStages,
  stages = STAGE_SCHEDULE as readonly StageDefinition[],
  progress = 0,
  className = '',
}: {
  currentIndex: number;
  completedStages: readonly number[];
  stages?: readonly StageDefinition[];
  /** 当前环节进度 0~1（用于充能条） */
  progress?: number;
  className?: string;
}) {
  const completed = new Set(completedStages);
  const clampedProgress = Math.max(0, Math.min(1, progress));
  return (
    <div
      data-testid="optical-track"
      className={`flex items-center gap-3 ${className}`}
      role="list"
      aria-label="16 环节进度"
    >
      <span className="micro-label shrink-0">
        STAGE {String(Math.max(currentIndex, 0)).padStart(2, '0')} / 16
      </span>
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        {stages.map((stage) => {
          const isCurrent = stage.index === currentIndex;
          const isDone = completed.has(stage.index);
          if (isCurrent) {
            return (
              <span
                key={stage.index}
                role="listitem"
                aria-current="step"
                title={`${String(stage.index).padStart(2, '0')} ${stage.name}`}
                className="flex shrink-0 items-center gap-1.5 rounded-full bg-white/[0.07] px-2 py-[2px] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.2),inset_0_0_0_1px_rgba(255,255,255,0.06)]"
              >
                <span className="track-node track-node--current" />
                <span className="mono hidden text-[9px] tracking-wide text-slate-200 sm:inline">
                  {String(stage.index).padStart(2, '0')} {stage.shortName}
                </span>
                <span className="relative h-[2px] w-8 overflow-hidden rounded-full bg-white/10 sm:w-12">
                  <span
                    className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-white/50 to-white/90 shadow-[0_0_6px_rgba(255,255,255,0.6)] transition-[width] duration-300"
                    style={{ width: `${clampedProgress * 100}%` }}
                  />
                </span>
              </span>
            );
          }
          return (
            <span
              key={stage.index}
              role="listitem"
              title={`${String(stage.index).padStart(2, '0')} ${stage.name}`}
              aria-label={`环节 ${stage.index} ${stage.name}`}
              className={
                isDone ? 'track-node track-node--done' : 'track-node track-node--future'
              }
            />
          );
        })}
      </div>
      <span className="hidden shrink-0 items-center gap-2 text-[10px] text-slate-500 lg:flex">
        <span className="status-dot status-dot--live" />
        <span className="mono tracking-widest">OPTICAL TRACK</span>
      </span>
    </div>
  );
}
